
import React, { useEffect, useRef } from 'react';

import {
    getAudioInputDeviceData,
    getAudioOutputDeviceData,
    setAudioInputDeviceAndUpdateSettings,
    setAudioOutputDevice as setAudioOutputDeviceAction
} from '../../../../base/devices';
import { InlineDialog } from '../../../../base/react';
import { connect } from '../../../../base/redux';
import {
    getCurrentMicDeviceId,
    getCurrentOutputDeviceId,
    getUserSelectedMicDeviceId,
    getUserSelectedOutputDeviceId
} from '../../../../base/settings';
import { isNoiseSuppressionEnabled, setNoiseSuppressionEnabledSetting } from '../../../../noise-suppression/functions';
import { setAudioSettingsVisibility } from '../../../actions';
import { getAudioSettingsVisibility } from '../../../functions';

import AudioSettingsContent, {
    type Props as AudioSettingsContentProps
} from './AudioSettingsContent';

type Props = AudioSettingsContentProps & {

  /**
   * Component's children (the audio button).
   */
  children: React$Node,

  /**
   * Flag controlling the visibility of the popup.
   */
  isOpen: boolean,

  /**
   * Callback executed when the popup closes.
   */
  onClose: Function,

  /**
   * Whether noise suppression is enabled or not.
   */
   noiseSuppressionEnabled: boolean,

  /**
   * Handler to toggle noise suppression.
   */
   onToggleNoiseSuppression: Function,

  /**
   * Device ID of the user selected microphone.
   */
   userSelectedMicDeviceId?: string,

  /**
   * Device ID of the user selected audio output device.
   */
   userSelectedAudioOutputDeviceId?: string,

  /**
   * Label of the user selected microphone.
   */
   userSelectedMicDeviceLabel?: string,

  /**
   * Label of the user selected audio output device.
   */
   userSelectedAudioOutputDeviceLabel?: string,
};

/**
 * Popup with audio settings.
 *
 * @returns {ReactElement}
 */
function AudioSettingsPopup({
    children,
    currentMicDeviceId,
    currentOutputDeviceId,
    isOpen,
    microphoneDevices,
    setAudioInputDevice,
    setAudioOutputDevice,
    onClose,
    outputDevices,
    noiseSuppressionEnabled,
    onToggleNoiseSuppression,
    userSelectedMicDeviceId,
    userSelectedAudioOutputDeviceId,
    userSelectedMicDeviceLabel,
    userSelectedAudioOutputDeviceLabel
}: Props) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const targetDocument = containerRef.current?.ownerDocument || document;
        const targetWindow = targetDocument.defaultView || window;
        const pipWindow = window.documentPictureInPicture?.window;
        const pipDocument = pipWindow?.document;

        let lastWidth = targetWindow.innerWidth;

        const handleResize = () => {
            if (targetWindow.innerWidth > lastWidth) {
                onClose();
            }
            lastWidth = targetWindow.innerWidth;
        };

        const handleClickOutside = event => {
            if (!containerRef.current) {
                return;
            }

            if (containerRef.current.contains(event.target)) {
                return;
            }

            onClose();
        };

        const docs = new Set();
        const wins = new Set();

        docs.add(targetDocument);
        wins.add(targetWindow);

        if (document) {
            docs.add(document);
        }
        if (window) {
            wins.add(window);
        }

        if (pipDocument) {
            docs.add(pipDocument);
        }
        if (pipWindow) {
            wins.add(pipWindow);
        }

        wins.forEach(win => {
            win.addEventListener('resize', handleResize);
        });

        docs.forEach(doc => {
            doc.addEventListener('click', handleClickOutside, true);
        });

        return () => {
            wins.forEach(win => {
                win.removeEventListener('resize', handleResize);
            });
            docs.forEach(doc => {
                doc.removeEventListener('click', handleClickOutside, true);
            });
        };
    }, [ isOpen, onClose ]);

    return (
        <div
            className = 'audio-preview'
            ref = { containerRef }>
            <InlineDialog
                content = {
                    <AudioSettingsContent
                        currentMicDeviceId = { currentMicDeviceId }
                        currentOutputDeviceId = { currentOutputDeviceId }
                        microphoneDevices = { microphoneDevices }
                        noiseSuppressionEnabled = { noiseSuppressionEnabled }
                        onToggleNoiseSuppression = { onToggleNoiseSuppression }
                        outputDevices = { outputDevices }
                        setAudioInputDevice = { setAudioInputDevice }
                        setAudioOutputDevice = { setAudioOutputDevice }
                        userSelectedAudioOutputDeviceId = { userSelectedAudioOutputDeviceId }
                        userSelectedAudioOutputDeviceLabel = { userSelectedAudioOutputDeviceLabel }
                        userSelectedMicDeviceId = { userSelectedMicDeviceId }
                        userSelectedMicDeviceLabel = { userSelectedMicDeviceLabel } />
                }
                isOpen = { isOpen }
                onClose = { onClose }
                placement = 'top-start'>
                {children}
            </InlineDialog>
        </div>
    );
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const settings = state['features/base/settings'];

    return {
        currentMicDeviceId: getCurrentMicDeviceId(state),
        currentOutputDeviceId: getCurrentOutputDeviceId(state),
        userSelectedMicDeviceId: getUserSelectedMicDeviceId(state) || settings?.userSelectedMicDeviceId,
        userSelectedAudioOutputDeviceId: getUserSelectedOutputDeviceId(state) || settings?.userSelectedAudioOutputDeviceId,
        userSelectedMicDeviceLabel: settings?.userSelectedMicDeviceLabel,
        userSelectedAudioOutputDeviceLabel: settings?.userSelectedAudioOutputDeviceLabel,
        isOpen: getAudioSettingsVisibility(state),
        microphoneDevices: getAudioInputDeviceData(state),
        outputDevices: getAudioOutputDeviceData(state),
        noiseSuppressionEnabled: isNoiseSuppressionEnabled(state)
    };
}

const mapDispatchToProps = {
    onClose: () => setAudioSettingsVisibility(false),
    setAudioInputDevice: setAudioInputDeviceAndUpdateSettings,
    setAudioOutputDevice: setAudioOutputDeviceAction,
    onToggleNoiseSuppression: setNoiseSuppressionEnabledSetting
};

export default connect(mapStateToProps, mapDispatchToProps)(AudioSettingsPopup);
